import FilterPill, { ChevronIcon } from './FilterPill';
import FilterPopover from './FilterPopover';
import { useState, useMemo } from 'react';
import { Select } from '@trackman/web-shared-components';
import { t } from 'utilities/TranslationUtils';
import KickLocationsCard, { ViewMode } from './KickLocationsCard';
import BallTrajectory3DChart, { BallTrajectoryData } from 'pages/Players/charts/BallTrajectory3DChart';
import {
  MeasurementMarkerDto,
  SessionKindGroupKey,
  SessionKindGroupName,
  SessionOptionDto,
} from 'types/models/SessionsPageData';
import { useSessionEnrichment } from 'utilities/useSessionEnrichment';
import { toTrajectory3DFromSamples } from 'utilities/kickTrajectoryUtils';
import { measurementResultBucket } from 'utilities/chartUtils';

type FavoriteState = 'all' | 'favorite';
type ResultOption = { value: string; label: string };
type SideFilterOption = 'left' | 'right';

/** Extended view type that adds the 3D trajectory tab to the base ViewMode. */
export type PitchPreviewView = ViewMode | '3d';

type Props = {
  title: string;
  selectedSessionLabel: string | null;
  onClearSelectedSession: () => void;
  playerId: string;
  sessionType: SessionKindGroupKey | 'all';
  result: string[];
  favorite: FavoriteState;
  players: { id: string; name: string }[];
  sessionTypeOptions: { value: SessionKindGroupKey | 'all'; label: string }[];
  resultOptions: ResultOption[];
  onPlayerChange: (playerId: string) => void;
  onSessionTypeChange: (sessionType: SessionKindGroupKey | 'all') => void;
  onResultChange: (result: string[]) => void;
  onFavoriteChange: (favorite: FavoriteState) => void;
  sideFilter?: Set<SideFilterOption>;
  onSideFilterChange?: (sides: Set<SideFilterOption>) => void;
  onReset: () => void;
  onOpenAnalysis: () => void;
  changedField: 'player' | 'sessionType' | 'result' | 'favorite' | 'metric' | null;
  flashCard: boolean;
  initialView: PitchPreviewView;
  initialViewKey: string;
  previewMeasurements: MeasurementMarkerDto[];
  sessions: SessionOptionDto[];
  selectedIds: Set<string>;
  onDragSelected: (measurementIds: string[]) => void;
  onToggleKick: (id: string) => void;
  onClearSelection?: () => void;
  hidePlayerControl?: boolean;
  hideSessionTypeControl?: boolean;
  hideAnalysisLink?: boolean;
  hideSideFilter?: boolean;
  /** The host renders the table itself, so the card offers only the top / goal / 3D views. */
  hideTableView?: boolean;
  /** Rendered in place of the top-view pitch chart. */
  topViewContent?: React.ReactNode;
  /** Hides the stat tiles under the chart (the host shows them elsewhere). */
  hideSummary?: boolean;
  /** Top view with the pitch turned 90° (goal on the left). */
  landscapeTop?: boolean;
  /** Never offer the 3D view (the host has its own place for it), even when kicks carry flight paths. */
  hide3DView?: boolean;
  /** False when the host knows there are no favourites to filter to: the "only favourite" pill is then hidden (unless it is on). */
  favoriteFilterAvailable?: boolean;
  /** Shows a removable "Selected N kicks manually" chip among the quick filters while kicks are drag-selected. */
  showSelectionChip?: boolean;
  /** Shows the result filter as a pill that opens a modal, instead of an inline dropdown. */
  resultFilterAsModal?: boolean;
  /** Extra filter pills rendered first in the quick-filter row (e.g. the zone layout). */
  extraFilters?: React.ReactNode;
  /** True while one of `extraFilters` narrows the kicks (counts towards the Reset chip). */
  extraFiltersActive?: boolean;
  /** One-line hint under the title. */
  subtitle?: string;
  /** Adds a chevron button (top right) that collapses the card to just its title and filters. */
  onToggleCollapse?: () => void;
  collapsed?: boolean;
  simplifiedKickModal?: boolean;
  getSessionKickCount?: (m: MeasurementMarkerDto) => number;
  onShowInTable?: (m: MeasurementMarkerDto) => void;
  onFilterSession?: (m: MeasurementMarkerDto) => void;
  kind?: SessionKindGroupName;
  initialComparisonId?: string | null;
};

const sharedMenuItemProps = {
  menuItem: {
    sx: {
      border: '1px solid transparent',
      '&.Mui-selected, &.Mui-selected:hover': {
        backgroundColor: 'rgba(249, 115, 22, 0.08)',
        borderColor: '#f97316',
      },
    },
    componentsProps: {
      selectedListItem: {
        sx: { display: 'none' },
      },
      selectedIcon: {
        style: { display: 'none' },
      },
    },
  },
};

const noShiftMenuProps = { disableScrollLock: true };

const PitchPreviewCard = ({
  title,
  selectedSessionLabel,
  onClearSelectedSession,
  playerId,
  sessionType,
  result,
  favorite,
  players,
  sessionTypeOptions,
  resultOptions,
  onPlayerChange,
  onSessionTypeChange,
  onResultChange,
  onFavoriteChange,
  sideFilter = new Set(),
  onSideFilterChange,
  onReset,
  onOpenAnalysis,
  changedField,
  flashCard,
  initialView,
  initialViewKey,
  previewMeasurements,
  sessions,
  selectedIds,
  onDragSelected,
  onToggleKick,
  onClearSelection,
  hidePlayerControl = false,
  hideSessionTypeControl = false,
  hideAnalysisLink = false,
  hideSideFilter = false,
  hideTableView = false,
  topViewContent,
  hideSummary = false,
  landscapeTop = false,
  hide3DView = false,
  favoriteFilterAvailable = true,
  showSelectionChip = false,
  resultFilterAsModal = false,
  extraFilters,
  extraFiltersActive = false,
  subtitle,
  onToggleCollapse,
  collapsed = false,
  simplifiedKickModal = false,
  getSessionKickCount,
  onShowInTable,
  onFilterSession,
  kind,
  initialComparisonId,
}: Props): React.ReactElement => {
  const [activeView, setActiveView] = useState<PitchPreviewView>(initialView);

  const allPlayersOption = { label: t('sessions.previewAllPlayers'), value: 'all' };

  const has3DData = useMemo(
    () => !hide3DView && previewMeasurements.some(m => m.trajectorySamples && m.trajectorySamples.length > 0),
    [previewMeasurements, hide3DView]
  );

  /** The first measurement that has usable trajectory samples, used for the 3D view. */
  const trajectory3DSource = useMemo<{ data: BallTrajectoryData; sessionId: string; measurementId: string } | null>(() => {
    if (!has3DData) return null;
    for (const m of previewMeasurements) {
      const traj = toTrajectory3DFromSamples(m);
      if (traj) return { data: traj, sessionId: m.sessionId, measurementId: m.id };
    }
    return null;
  }, [previewMeasurements, has3DData]);
  const trajectory3D = trajectory3DSource?.data ?? null;

  /** Normalise the active view so that '3d' is only valid when there is 3D data. */
  const effectiveView: PitchPreviewView =
    activeView === '3d' && !has3DData ? 'top' : activeView;

  // SharedCalculator enrichment (free-kick wall + targets) for the 3D view — the same endpoint the
  // Measurement Detail page uses. Fetched lazily for the displayed kick when the 3D tab is active.
  const enrichment = useSessionEnrichment(trajectory3DSource?.sessionId, trajectory3DSource?.measurementId, effectiveView === '3d');

  /** The view passed down to KickLocationsCard must be a base ViewMode. */
  const kickLocationsView: ViewMode =
    effectiveView === '3d' ? 'top' : effectiveView;

  const SESSION_TYPE_TO_GROUP: Partial<Record<SessionKindGroupKey, string>> = {
    freekick: 'FreeKick',
    penalty: 'Penalty',
    shooting: 'Shooting',
    corner: 'Corner',
    goalkeeper: 'Goalkeeper',
    throwin: 'ThrowIn',
  };

  const sessionGroup = sessionType === 'all' ? null : SESSION_TYPE_TO_GROUP[sessionType] ?? null;
  const isPenalty = sessionGroup === 'Penalty';
  const isLowVolume = sessionGroup === 'Corner' || sessionGroup === 'ThrowIn' || sessionGroup === 'Goalkeeper';
  const onTargetGoalsFilterKeys = resultOptions
    .map((option) => option.value)
    .filter((value) => {
      const parts = value.split('::');
      const resultKey = parts.length > 1 ? parts[1] : '';
      // Penalties: "on target" includes every non-off-target result (Goal, Savable, Unsavable,
      // OnTarget) per the shared result classifier, since goals are a subset of on-target kicks.
      if (isPenalty) return measurementResultBucket({ result: resultKey, kind: sessionGroup }) !== 'offTarget';
      return ['Goal', 'OnTarget'].includes(resultKey);
    });
  const canShowOnTargetGoalsPill = onTargetGoalsFilterKeys.length > 0;
  const onTargetGoalsPillLabelKey = isPenalty || isLowVolume ? 'sessions.previewShowOnlyOnTarget' : 'sessions.previewShowOnlyOnTargetGoals';

  const [resultModalOpen, setResultModalOpen] = useState(false);
  const resultLabelByValue = new Map(resultOptions.map((option) => [option.value, option.label] as const));
  const allResultValues = resultOptions.map((option) => option.value);
  const selectedResultValues = result;
  const effectiveResultValues = selectedResultValues.length === 0 ? allResultValues : selectedResultValues;
  const isOnTargetGoalsOnlyActive = onTargetGoalsFilterKeys.length > 0
    && selectedResultValues.length === onTargetGoalsFilterKeys.length
    && onTargetGoalsFilterKeys.every((value) => selectedResultValues.includes(value));
  
  // Side filter state (left/right for Corner and ThrowIn).
  const canShowSideFilter = sessionGroup === 'Corner' || sessionGroup === 'ThrowIn';
  const isLeftSideActive = sideFilter.has('left');
  const isRightSideActive = sideFilter.has('right');

  // Player / session type only count as filters when the card lets the user change them; when the host fixes them
  // (Player Performance is always one player and one kind) they are not something "reset" would undo.
  const resultPillLabel = selectedResultValues.length === 0 || selectedResultValues.length === allResultValues.length
    ? t('sessions.previewAllResults')
    : selectedResultValues.length === 1
      ? (resultLabelByValue.get(selectedResultValues[0]) ?? selectedResultValues[0])
      : t('sessions.previewResultsSelected', { count: selectedResultValues.length });

  const hasCustomFilters = (!hidePlayerControl && playerId !== 'all')
    || (!hideSessionTypeControl && sessionType !== 'all')
    || favorite !== 'all'
    || selectedSessionLabel !== null
    || (selectedResultValues.length > 0 && selectedResultValues.length !== allResultValues.length)
    || sideFilter.size > 0
    || (showSelectionChip && selectedIds.size > 0) // a manual selection is a filter too
    || extraFiltersActive;

  return (
    <section className={`sessions-preview-card${flashCard ? ' sessions-preview-card--flash' : ''}${collapsed ? ' sessions-preview-card--collapsed' : ''}`}>
      {onToggleCollapse && (
        <button
          type='button'
          className='sessions-preview-collapse'
          onClick={onToggleCollapse}
          aria-expanded={!collapsed}
          aria-label={t(collapsed ? 'playerPerformance.overviewExpand' : 'playerPerformance.overviewCollapse')}
          title={t(collapsed ? 'playerPerformance.overviewExpand' : 'playerPerformance.overviewCollapse')}
          data-test='pp-overview-collapse'
        >
          <ChevronIcon width={14} height={9} />
        </button>
      )}
      <div className='sessions-preview-header'>
        <div className='sessions-preview-header-copy'>
          <h3 className='sessions-preview-title'>{title}</h3>
          {subtitle && <p className='sessions-preview-subtitle'>{subtitle}</p>}
        </div>
        <div className='sessions-preview-header-actions'>
          {hasCustomFilters && !resultFilterAsModal && (
            <button type='button' className='sessions-preview-reset-btn sessions-preview-reset-btn--active' onClick={onReset}>{t('sessions.resetFilters')}</button>
          )}
          {!hideAnalysisLink && (
            <button type='button' className='sessions-preview-open-btn' onClick={onOpenAnalysis}>
              {t('sessions.previewOpenAnalysis')}
            </button>
          )}
        </div>
      </div>

      <div className='sessions-preview-controls'>
      <div className='sessions-preview-filter-bar'>
        {!hidePlayerControl && (
          <label className={`sessions-preview-filter${changedField === 'player' ? ' sessions-preview-filter--pulse' : ''}`}>
            <span>{t('sessions.previewPlayer')}</span>
            <Select
              value={playerId}
              size='small'
              width='100%'
              MenuProps={noShiftMenuProps}
              componentsProps={sharedMenuItemProps}
              onChange={(e) => onPlayerChange(String(e.target.value))}
              selectOptions={[allPlayersOption, ...players.map((player) => ({ label: player.name, value: player.id }))]}
            />
          </label>
        )}

        {!hideSessionTypeControl && (
          <label className={`sessions-preview-filter${changedField === 'sessionType' ? ' sessions-preview-filter--pulse' : ''}`}>
            <span>{t('sessions.previewSessionType')}</span>
            <Select
              value={sessionType}
              size='small'
              width='100%'
              MenuProps={noShiftMenuProps}
              componentsProps={sharedMenuItemProps}
              onChange={(e) => onSessionTypeChange(e.target.value as SessionKindGroupKey | 'all')}
              selectOptions={sessionTypeOptions.map((option) => ({ label: option.label, value: option.value }))}
            />
          </label>
        )}

        {!resultFilterAsModal && (
        <label className={`sessions-preview-filter${changedField === 'result' ? ' sessions-preview-filter--pulse' : ''}`}>
          <span>{t('sessions.previewResult')}</span>
          <Select
            value={effectiveResultValues}
            multiple
            size='small'
            width='100%'
            MenuProps={noShiftMenuProps}
            componentsProps={sharedMenuItemProps}
            renderValue={(selected) => {
              const selectedValues = (selected as string[]) ?? [];
              if (selectedValues.length === 0 || selectedValues.length === allResultValues.length) return t('sessions.previewAllResults');
              if (selectedValues.length === 1) return resultLabelByValue.get(selectedValues[0]) ?? selectedValues[0];
              return t('sessions.previewResultsSelected', { count: selectedValues.length });
            }}
            onChange={(e) => {
              const value = e.target.value;
              const selectedValues = Array.isArray(value)
                ? value.map((item) => String(item)).filter((item) => resultLabelByValue.has(item))
                : (typeof value === 'string' ? value.split(',').filter((item) => resultLabelByValue.has(item)) : []);
              // Store empty array as canonical "all" state.
              if (selectedValues.length === 0 || selectedValues.length === allResultValues.length) {
                onResultChange([]);
                return;
              }
              if (Array.isArray(value)) {
                onResultChange(selectedValues);
                return;
              }
              onResultChange(selectedValues);
            }}
            selectOptions={resultOptions}
          />
        </label>
        )}

      </div>

      <div className={`sessions-preview-smart-pills${changedField === 'favorite' || changedField === 'result' ? ' sessions-preview-smart-pills--pulse' : ''}`}>
        {extraFilters}
        {resultFilterAsModal && (
          <FilterPill
            label={resultPillLabel}
            active={selectedResultValues.length > 0}
            open={resultModalOpen}
            onToggle={() => setResultModalOpen((v) => !v)}
            dataTest='pp-result-filter'
          >
            <FilterPopover
              options={resultOptions}
              selected={selectedResultValues}
              onChange={onResultChange}
              onClose={() => setResultModalOpen(false)}
            />
          </FilterPill>
        )}
        {selectedSessionLabel && (
          <button
            type='button'
            className='sessions-preview-smart-pill sessions-preview-smart-pill--active sessions-preview-smart-pill--chip sessions-preview-smart-pill--session'
            onClick={onClearSelectedSession}
          >
            <span>{t('sessions.previewShowingSessionFrom', { label: selectedSessionLabel })}</span>
            <span className='sessions-preview-smart-pill-close' aria-hidden='true'>&times;</span>
          </button>
        )}

        {showSelectionChip && selectedIds.size > 0 && onClearSelection && (
          <button
            type='button'
            className='sessions-preview-smart-pill sessions-preview-smart-pill--active sessions-preview-smart-pill--chip'
            onClick={onClearSelection}
            data-test='pp-selection-chip'
          >
            <span>{t('sessions.previewSelectedManually', { count: selectedIds.size })}</span>
            <span className='sessions-preview-smart-pill-close' aria-hidden='true'>&times;</span>
          </button>
        )}

        {(favoriteFilterAvailable || favorite === 'favorite') && (
          <button
            type='button'
            className={`sessions-preview-smart-pill${favorite === 'favorite' ? ' sessions-preview-smart-pill--active' : ''}`}
            onClick={() => onFavoriteChange(favorite === 'favorite' ? 'all' : 'favorite')}
          >
            {t('sessions.previewShowOnlyFavorite')}
          </button>
        )}

        {canShowOnTargetGoalsPill && (
          <button
            type='button'
            className={`sessions-preview-smart-pill${isOnTargetGoalsOnlyActive ? ' sessions-preview-smart-pill--active' : ''}`}
            onClick={() => onResultChange(isOnTargetGoalsOnlyActive ? [] : onTargetGoalsFilterKeys)}
          >
            {t(onTargetGoalsPillLabelKey)}
          </button>
        )}

        {canShowSideFilter && !hideSideFilter && onSideFilterChange && (
          <>
            <button
              type='button'
              className={`sessions-preview-smart-pill${isLeftSideActive ? ' sessions-preview-smart-pill--active' : ''}`}
              onClick={() => {
                onSideFilterChange(new Set<SideFilterOption>(['left']));
              }}
            >
              {t('sessions.previewShowOnlyLeftSide')}
            </button>
            <button
              type='button'
              className={`sessions-preview-smart-pill${isRightSideActive ? ' sessions-preview-smart-pill--active' : ''}`}
              onClick={() => {
                onSideFilterChange(new Set<SideFilterOption>(['right']));
              }}
            >
              {t('sessions.previewShowOnlyRightSide')}
            </button>
          </>
        )}
        {resultFilterAsModal && hasCustomFilters && (
          <button
            type='button'
            className='sessions-preview-smart-pill sessions-preview-smart-pill--reset'
            onClick={onReset}
            data-test='pp-reset-filters'
          >
            <span>{t('sessions.resetFilters')}</span>
            <span className='sessions-preview-smart-pill-close' aria-hidden='true'>&times;</span>
          </button>
        )}
      </div>
      </div>

      <div className='sessions-preview-chart-shell'>
        {has3DData && (
          <div className='sessions-preview-view-tab-bar'>
            {(['top', 'goal', 'table', '3d'] as const).map((v) => {
              if (v === 'table') return null;
              return (
                <button
                  key={v}
                  type='button'
                  className={`sessions-preview-view-tab${effectiveView === v ? ' sessions-preview-view-tab--active' : ''}`}
                  onClick={() => setActiveView(v)}
                >
                  {v === 'top' ? t('sessions.viewTop') : v === 'goal' ? t('sessions.viewGoal') : t('sessions.view3D')}
                </button>
              );
            })}
          </div>
        )}

        {effectiveView !== '3d' && (
          <KickLocationsCard
            key={initialViewKey}
            data={previewMeasurements}
            sessions={sessions}
            selectedIds={selectedIds}
            hoveredId={null}
            pitchMode='origin'
            onPointHover={() => undefined}
            onToggleKick={onToggleKick}
            onDragSelected={onDragSelected}
            onClearSelection={onClearSelection}
            selectionUiEnabled
            selectionInteractionsEnabled
            showTableViewTab={!has3DData}
            hideTableView={hideTableView}
            topViewContent={topViewContent}
            hideSummary={hideSummary}
            landscapeTop={landscapeTop}
            initialView={kickLocationsView}
            performanceMode={false}
            allowSessionDetailsFetch
            simplifiedKickModal={simplifiedKickModal}
            getSessionKickCount={getSessionKickCount}
            onShowInTable={onShowInTable}
            onFilterSession={onFilterSession}
            kind={kind}
            initialComparisonId={initialComparisonId}
          />
        )}

        {effectiveView === '3d' && (
          <div className='sessions-preview-trajectory-3d'>
            {trajectory3D ? (
              <BallTrajectory3DChart
                trajectory={trajectory3D}
                wallPlayers={enrichment?.wallPlayers}
                targets={enrichment?.targets}
              />
            ) : (
              <div className='sessions-preview-empty-overlay'>
                <div className='sessions-preview-empty'>
                  <p>{t('sessions.noTrajectoryData')}</p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default PitchPreviewCard;
